import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/** Overlays owned by the shell. Pages open them through `useOverlays()`. */
export type OverlayId = 'vehicleSearch' | 'experienceSearch' | 'account' | 'preferences';

interface Overlays {
  current: OverlayId | null;
  open: (id: OverlayId) => void;
  toggle: (id: OverlayId) => void;
  close: () => void;
}

const Ctx = createContext<Overlays | null>(null);

export function OverlayProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<OverlayId | null>(null);
  const open = useCallback((id: OverlayId) => setCurrent(id), []);
  const toggle = useCallback((id: OverlayId) => setCurrent((c) => (c === id ? null : id)), []);
  const close = useCallback(() => setCurrent(null), []);
  const value = useMemo(() => ({ current, open, toggle, close }), [current, open, toggle, close]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOverlays(): Overlays {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useOverlays must be used inside <OverlayProvider>');
  return ctx;
}
